/**
 * Avatar URL Utilities
 *
 * Handles conversion between GLB, PNG, and JSON URLs for avatars.
 * Uses local (self-hosted) assets, falls back to first library avatar if not found.
 */

import { patientAvatarLibrary, doctorAvatarLibrary } from '../Constants/avatarLibrary';

// Base URL for RPM assets - can be overridden via environment variable
// Default to empty string for relative paths (localhost), or set to GCP bucket URL
export const RPM_ASSETS_BASE_URL = process.env.REACT_APP_RPM_ASSETS_BASE_URL || '';

// Build set of known local avatar IDs
const LOCAL_AVATAR_IDS = new Set<string>();
const ALL_LIBRARY_URLS = [...patientAvatarLibrary, ...doctorAvatarLibrary];

ALL_LIBRARY_URLS.forEach(url => {
  const id = extractAvatarId(url);
  if (id) LOCAL_AVATAR_IDS.add(id);
});

// Fallback avatar ID (first patient avatar)
const FALLBACK_AVATAR_ID = extractAvatarId(patientAvatarLibrary[0]) || '65c2d56a5bfe15dcc69fa43b';

/**
 * Extract avatar ID from any avatar URL (GLB, PNG, or JSON)
 */
export function extractAvatarId(url: string): string | null {
  if (!url) return null;

  // Local path: /rpm-assets/glb/abc123.glb or /rpm-assets/png/abc123-happy.png
  const localMatch = url.match(/\/rpm-assets\/(?:glb|png|json)\/([a-f0-9]{20,40})/i);
  if (localMatch) return localMatch[1];

  // GCP bucket: https://storage.googleapis.com/goodthoughts-avatars/glb/abc123.glb
  const gcpMatch = url.match(/storage\.googleapis\.com\/[^/]+\/(?:glb|png|json)\/([a-f0-9]{20,40})/i);
  if (gcpMatch) return gcpMatch[1];

  // RPM CDN: https://models.readyplayer.me/abc123.glb
  const rpmModelsMatch = url.match(/models\.readyplayer\.me\/([a-f0-9]{20,40})/i);
  if (rpmModelsMatch) return rpmModelsMatch[1];

  // RPM API: https://api.readyplayer.me/v1/avatars/abc123.png
  const rpmApiMatch = url.match(/api\.readyplayer\.me\/v1\/avatars\/([a-f0-9]{20,40})/i);
  if (rpmApiMatch) return rpmApiMatch[1];

  // RPM Gallery: https://readyplayer.me/gallery/abc123
  const rpmGalleryMatch = url.match(/readyplayer\.me\/gallery\/([a-f0-9]{20,40})/i);
  if (rpmGalleryMatch) return rpmGalleryMatch[1];

  return null;
}

/**
 * Check if an avatar ID is available locally
 */
export function isLocalAvatar(urlOrId: string): boolean {
  const id = urlOrId.includes('/') ? extractAvatarId(urlOrId) : urlOrId;
  return id ? LOCAL_AVATAR_IDS.has(id) : false;
}

/**
 * Get the avatar ID to use - returns the ID if local, otherwise fallback
 */
function getLocalIdOrFallback(urlOrId: string): string {
  const id = urlOrId.includes('/') ? extractAvatarId(urlOrId) : urlOrId;
  if (id && LOCAL_AVATAR_IDS.has(id)) {
    return id;
  }
  return FALLBACK_AVATAR_ID;
}

/**
 * Get the GLB URL for an avatar (local if available, otherwise fallback)
 * Note: GCP bucket doesn't have /rpm-assets prefix, localhost does
 */
export function getGlbUrl(urlOrId: string): string {
  const id = getLocalIdOrFallback(urlOrId);
  const path = RPM_ASSETS_BASE_URL ? `/glb/${id}.glb` : `/rpm-assets/glb/${id}.glb`;
  return `${RPM_ASSETS_BASE_URL}${path}`;
}

/**
 * Get the PNG URL for an avatar with optional expression and pose
 * Filename format: {id}-{camera}[-{expression}][-{pose}].png
 * Default camera is 'portrait'
 */
export function getPngUrl(
  urlOrId: string,
  options?: {
    expression?: 'happy' | 'lol' | 'sad' | 'scared' | 'rage';
    pose?: 'power-stance' | 'relaxed' | 'standing' | 'thumbs-up';
    camera?: 'portrait' | 'fullbody' | 'fit';
  }
): string {
  const id = getLocalIdOrFallback(urlOrId);

  // Build filename: {id}-{camera}[-{expression}][-{pose}].png
  const camera = options?.camera || 'portrait';
  const parts: string[] = [camera];
  if (options?.expression) parts.push(options.expression);
  if (options?.pose) parts.push(options.pose);

  const path = RPM_ASSETS_BASE_URL ? `/png/${id}-${parts.join('-')}.png` : `/rpm-assets/png/${id}-${parts.join('-')}.png`;
  return `${RPM_ASSETS_BASE_URL}${path}`;
}

/**
 * Get the JSON metadata URL for an avatar
 * Note: GCP bucket doesn't have /rpm-assets prefix, localhost does
 */
export function getJsonUrl(urlOrId: string): string {
  const id = getLocalIdOrFallback(urlOrId);
  const path = RPM_ASSETS_BASE_URL ? `/json/${id}.json` : `/rpm-assets/json/${id}.json`;
  return `${RPM_ASSETS_BASE_URL}${path}`;
}

/**
 * Convert GLB URL to PNG URL (handles both local and remote input)
 */
export function glbToPng(
  glbUrl: string,
  options?: Parameters<typeof getPngUrl>[1]
): string {
  return getPngUrl(glbUrl, options);
}

/**
 * Get all available local avatar IDs
 */
export function getLocalAvatarIds(): string[] {
  return Array.from(LOCAL_AVATAR_IDS);
}
